import React from "react";
import PropTypes from "prop-types";
import * as R from "ramda";

import Header from "components/Header";
import Loader from "components/Loader";
import HotKeys from "components/guides/HotKeys";
import Peekbar from "components/guides/Peekbar";
import SendMessageBadge from "components/messaging/SendMessageBadge";
import HelpBar from "components/projects/HelpBar";
import SummaryTable from "components/projects/SummaryTable";
import * as Layout from "components/projects/pages/Layout";
import RequirementFooter from "components/projects/requirement/RequirementFooter";
import Section from "components/projects/requirement/Section";
import SubHeader from "components/projects/requirement/SubHeader";
import ZoningBar from "components/zoning/ZoningBar";
import { AnswerContextWithContext } from "contexts/AnswerContextProvider";
import { useAnswerContext } from "contexts/answerContext";
import useParam from "hooks/useParam";
import { getRequirementSectionsList, getUnansweredCountForFieldIdents } from "queries/projects";
import { useRequirementBySlug } from "queries/requirements";
import { getLockedFieldIDs, getRequirementApplicationByRequirementID } from "reducers/projects";
import { getRequirementStepCount } from "reducers/requirements";

import styles from "./RequirementFormFillingPage.scss";
import Step from "./requirement/Step";
import useIsDirectApply from "./useIsDirectApply";

const RequirementFormFillingPage = () => {
  const { record: project } = useAnswerContext();
  const requirementSlug = useParam("requirementSlug");
  const { isDirectApply } = useIsDirectApply();

  const { data: requirement, isLoading } = useRequirementBySlug(requirementSlug);

  if (isLoading) {
    return <Loader />;
  }
  const sections = getRequirementSectionsList(project, requirement);

  const application = getRequirementApplicationByRequirementID(project, requirement.id);

  const steps = getRequirementStepCount(requirement);
  const lockedFieldIDs = getLockedFieldIDs(project);

  const tableProps = {
    sticky: true,
    requirementID: requirement.id,
  };

  if (isDirectApply) {
    const feeSection = R.find(R.propEq("fee_scoping", "key"))(sections);
    const feeFields = R.propOr([], "fieldIdents", feeSection);
    tableProps.unansweredFeeFieldsCount = getUnansweredCountForFieldIdents(project, feeFields);
  }

  return (
    <AnswerContextWithContext context={requirement}>
      <HotKeys />
      <Layout.Page name="requirement-form">
        <Header hideTitle />
        {isDirectApply || <SubHeader requirement={requirement} />}
        <Layout.Main>
          <Step
            header={requirement.name}
            instructions={requirement.form_intro}
            instructionsKey="projects.requirement.apply.instructions"
            step={1}
            steps={steps}
          />
          <Layout.ContentWithSidebar>
            <Layout.Content>
              {sections.map((section) => (
                <Section section={section} key={section.name} lockedFieldIDs={lockedFieldIDs} />
              ))}
            </Layout.Content>
            <Layout.Sidebar>
              <SummaryTable {...tableProps} />
              <div className={styles.sendMessageBadge}>
                <SendMessageBadge application={application} />
              </div>
            </Layout.Sidebar>
          </Layout.ContentWithSidebar>
          <HelpBar />
        </Layout.Main>
        <ZoningBar />
        <Peekbar showFees requirementID={requirement.id} />
        <RequirementFooter
          requirement={requirement}
          disableBack={isDirectApply}
          nextTextKey="submit_application"
        />
      </Layout.Page>
    </AnswerContextWithContext>
  );
};
RequirementFormFillingPage.propTypes = {
  directApply: PropTypes.bool,
};

export default RequirementFormFillingPage;
